import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["name", "fullWidth", "color", "label", "bottomLabel", "error", "maskProps", "focused", "inputRef", "labelProps", "fieldProps", "containerProps", "type", "clearable", "value"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import { TextMask } from '@elements/form/fields/text-mask';
import { Icon } from '@elements/icon';
import { StyledClearableInputEndAdornment, StyledFormControl, StyledFormHelperErrorText, StyledFormHelperText, StyledInput, StyledInputBase, StyledInputEndAdornment, StyledInputLabel, StyledInputStartAdornment, StyleFormHelperWrapper } from './text-field.styles';
import clear from '@images/clear.svg';
import eyeOff from '@images/eye-off.svg';
import eyeOn from '@images/eye-on.svg';
import search from '@images/search.svg';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

/**
 * TextField component.
 *
 * @author Mikhail Siutsou
 * @function TextField
 * @category components
 * @return {JSX.Element} textfield component.
 */
var TextField = function TextField(_ref) {
  var name = _ref.name,
      fullWidth = _ref.fullWidth,
      color = _ref.color,
      label = _ref.label,
      bottomLabel = _ref.bottomLabel,
      error = _ref.error,
      maskProps = _ref.maskProps,
      focused = _ref.focused,
      inputRef = _ref.inputRef,
      labelProps = _ref.labelProps,
      fieldProps = _ref.fieldProps,
      containerProps = _ref.containerProps,
      type = _ref.type,
      clearable = _ref.clearable,
      value = _ref.value,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isShowPassword = _useState2[0],
      setShowPassword = _useState2[1];

  var inputName = name && "".concat(name, "-textfield");
  var Component = restProps.variant === 'outlined' ? StyledInput : StyledInputBase;
  var isError = !!error;
  var literalProps = {
    htmlFor: inputName,
    id: inputName,
    errorId: inputName && "".concat(inputName, "-error-text")
  };
  var TextFieldMaskProps = maskProps ? {
    inputComponent: TextMask,
    inputProps: maskProps
  } : {};
  /**
   * handle change state visible password.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */

  var handleClickShowPassword = function handleClickShowPassword(event) {
    event.preventDefault();
    setShowPassword(function (prevValue) {
      return !prevValue;
    });
  };
  /**
   * handle mouse up event.
   *
   * @author Ihar Kazlouski
   * @param { MouseEvent<HTMLButtonElement>} event mouse event.
   * @return {void}
   */


  var handleMouseUpPassword = function handleMouseUpPassword(event) {
    event.preventDefault();
  };
  /**
   * Clear handler.
   *
   * @author Mikhail Siutsou
   * @function clearHandler
   */


  var clearHandler = function clearHandler() {
    if (restProps !== null && restProps !== void 0 && restProps.onChange && typeof restProps.onChange === 'function') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      restProps.onChange(new Event('clear'), '');
    }
  };

  return /*#__PURE__*/_jsx(StyledFormControl, {
    fullWidth: fullWidth,
    focused: focused,
    children: /*#__PURE__*/_jsxs(Grid, _objectSpread(_objectSpread({
      container: true,
      rowSpacing: 0.5,
      columnSpacing: 2,
      alignItems: "center"
    }, containerProps), {}, {
      children: [label && /*#__PURE__*/_jsx(Grid, _objectSpread(_objectSpread({
        item: true,
        xs: 12
      }, labelProps), {}, {
        children: /*#__PURE__*/_jsx(StyledInputLabel, {
          shrink: true,
          htmlFor: literalProps.htmlFor,
          children: label
        })
      })), /*#__PURE__*/_jsx(Grid, _objectSpread(_objectSpread({
        item: true,
        flex: "1 1"
      }, fieldProps), {}, {
        children: /*#__PURE__*/_jsxs(Box, {
          position: "relative",
          children: [/*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({
            inputRef: inputRef,
            error: isError,
            id: literalProps.id,
            "aria-describedby": inputName,
            value: value,
            name: name,
            fullWidth: fullWidth,
            color: color,
            type: type === 'password' ? isShowPassword ? 'text' : 'password' : type,
            startAdornment: type === 'search' ? /*#__PURE__*/_jsx(StyledInputStartAdornment, {
              position: "start",
              children: /*#__PURE__*/_jsx(Icon, {
                src: search,
                width: 24,
                height: 24
              })
            }) : null,
            endAdornment: type === 'password' ? /*#__PURE__*/_jsx(StyledInputEndAdornment, {
              position: "start",
              children: /*#__PURE__*/_jsx(IconButton, {
                "aria-label": "toggle password visibility",
                onClick: handleClickShowPassword,
                onMouseUp: handleMouseUpPassword,
                color: "primary",
                children: isShowPassword ? /*#__PURE__*/_jsx(Icon, {
                  src: eyeOn,
                  width: 24,
                  height: 24
                }) : /*#__PURE__*/_jsx(Icon, {
                  src: eyeOff,
                  width: 24,
                  height: 24
                })
              })
            }) : clearable && value.length > 0 ? /*#__PURE__*/_jsx(StyledClearableInputEndAdornment, {
              position: "end",
              children: /*#__PURE__*/_jsx(IconButton, {
                onClick: clearHandler,
                children: /*#__PURE__*/_jsx(Icon, {
                  src: clear,
                  width: 20,
                  height: 20
                })
              })
            }) : null
          }, TextFieldMaskProps), restProps)), isError && /*#__PURE__*/_jsx(StyleFormHelperWrapper, {
            children: /*#__PURE__*/_jsx(StyledFormHelperErrorText, {
              id: literalProps.errorId,
              children: error
            })
          }), bottomLabel && !isError ? /*#__PURE__*/_jsx(StyleFormHelperWrapper, {
            children: /*#__PURE__*/_jsx(StyledFormHelperText, {
              children: bottomLabel
            })
          }) : null]
        })
      }))]
    }))
  });
};

export { TextField };